import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PictureAsPdfRounded as PdfIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ReportSortControl from "../../PawnShop/reportSort";
import { reportFixedSx, reportResultsCardSx } from "../../PawnShop/reportLayout";

/**
 * The pieces the three pawn ledger reports are built from, so they look like
 * the pawn Report screens (Payment / Day Book / …) rather than like the Travel
 * ERP screens they were forked from — one filter card with a View button, one
 * results card that flexes into the leftover height, the same table cell
 * styling, and the same PDF letterhead.
 *
 * The Daily Tick / Travel copies of these reports are untouched: only the pawn
 * host renders this folder. See ../Ledger.js.
 */

// ─── Blue/White Design Tokens (shared with the pawn report screens) ──────────
export const BLUE = "#2563EB";
export const BLUE_DARK = "#1D4ED8";
export const BLUE_PALE = "#EFF6FF";
export const INK = "#0F172A";
export const INK_SOFT = "#334155";
export const INK_MUTED = "#64748B";
export const BORDER = "#E2E8F0";
export const SURFACE = "#FFFFFF";
export const SUCCESS = "#059669";
export const DANGER = "#DC2626";
export const HEAD_BG = "#F8FAFC";
// The page ground the pawn report screens sit on.
export const BG_ALT = "#F1F5F9";

export const TODAY = () => dayjs().format("YYYY-MM-DD");

/**
 * The first day of the current month — where every date window on these reports
 * opens.
 *
 * It used to be the first day of the financial year, which meant the ledger
 * always loaded eleven months of postings before anyone had asked for them.
 * The month to date is what a clerk actually wants on opening the screen, and
 * unlike the FY it needs nothing from the session context, so there is no
 * "adopt it once the FY arrives" dance either.
 */
export const monthStart = () => dayjs().startOf("month").format("YYYY-MM-DD");

// ─── Amount / date formatting ────────────────────────────────────────────────
// A zero cell stays EMPTY on entry rows, matching the printed register where
// only the side that moved carries a figure. Balance and total rows use
// `fmtAmtAlways`: a nil balance is a statement, not missing data.
export const fmtAmt = (v) => {
  const n = parseFloat(v);
  if (!n) return "";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const fmtAmtAlways = (v) =>
  (parseFloat(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** "1,23,456.00 Dr" — the register's own way of writing a signed balance. */
export const fmtBalance = (v, type) =>
  `${fmtAmtAlways(v)} ${type === "CREDIT" ? "Cr" : "Dr"}`;

export const fmtDate = (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "");

/** "20-08-2026" or "01-04-2026 to 20-08-2026" — one label for both modes. */
export const rangeLabel = (from, to) =>
  from === to ? fmtDate(from) : `${fmtDate(from)} to ${fmtDate(to)}`;

// ─── Table cell styling (identical to the pawn report screens) ───────────────
export const headCellSx = {
  bgcolor: HEAD_BG,
  color: INK_MUTED,
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  borderBottom: `1px solid ${BORDER}`,
  whiteSpace: "nowrap",
};

export const cellSx = {
  fontSize: "0.8rem",
  color: INK_SOFT,
  borderBottom: `1px solid ${BORDER}`,
  py: 0.6,
  verticalAlign: "middle",
};

/** The totals row, pinned to the bottom edge of the scrolling table area. */
export const footCellSx = {
  position: "sticky",
  bottom: 0,
  bgcolor: HEAD_BG,
  fontWeight: 800,
  color: INK,
  fontSize: "0.82rem",
  borderTop: `2px solid ${BORDER}`,
};

// ─── Card shells ─────────────────────────────────────────────────────────────
export const filterCardSx = {
  border: `1px solid ${BORDER}`,
  borderRadius: 3,
  mb: 2,
  bgcolor: SURFACE,
  ...reportFixedSx,
};

export const filterCardContentSx = {
  px: { xs: 1.5, md: 2 },
  py: { xs: 1.5, md: 2 },
  "&:last-child": { pb: { xs: 1.5, md: 2 } },
};

export const resultsCardSx = {
  border: `1px solid ${BORDER}`,
  borderRadius: 3,
  bgcolor: SURFACE,
  ...reportResultsCardSx,
};

/** Wraps the filter fields; every report opens with the same shell. */
export const FilterCard = ({ children }) => (
  <Card elevation={0} sx={filterCardSx}>
    <CardContent sx={filterCardContentSx}>{children}</CardContent>
  </Card>
);

/**
 * View + sort + reset — the trio that closes every pawn report's filter row.
 * View is a manual refresh here: every filter on these screens is a picker, and
 * pickers already auto-fetch (see feedback_report_auto_fetch_on_date).
 */
export const FilterActions = ({
  onView,
  onReset,
  loading,
  sortOptions,
  sort,
  onSortChange,
}) => (
  <Stack direction="row" spacing={1}>
    <Button
      variant="contained"
      onClick={onView}
      disabled={loading}
      startIcon={
        loading ? (
          <CircularProgress size={16} sx={{ color: "#fff" }} />
        ) : (
          <SearchIcon />
        )
      }
      sx={{
        bgcolor: BLUE,
        textTransform: "none",
        fontWeight: 700,
        boxShadow: "none",
        "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
      }}
    >
      View
    </Button>
    {sortOptions && (
      <ReportSortControl
        options={sortOptions}
        value={sort}
        onChange={onSortChange}
      />
    )}
    <Tooltip title="Reset filters">
      <IconButton
        onClick={onReset}
        sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}
      >
        <RefreshIcon sx={{ color: INK_MUTED }} />
      </IconButton>
    </Tooltip>
  </Stack>
);

/** Results-card header: title + subtitle on the left, count chip + PDF right. */
export const ResultsHeader = ({ title, subtitle, count, onPdf, pdfDisabled }) => (
  <Box
    sx={{
      px: { xs: 1.5, md: 2 },
      py: 1.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1,
      borderBottom: `1px solid ${BORDER}`,
      ...reportFixedSx,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, color: INK, fontSize: "0.95rem" }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
    <Stack direction="row" spacing={1.5} alignItems="center">
      {count ? (
        <Chip
          label={count}
          size="small"
          sx={{
            bgcolor: BLUE_PALE,
            color: BLUE_DARK,
            fontWeight: 700,
            fontSize: "0.72rem",
          }}
        />
      ) : null}
      <Button
        variant="outlined"
        size="small"
        onClick={onPdf}
        disabled={pdfDisabled}
        startIcon={<PdfIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          color: BLUE,
          borderColor: BLUE,
          "&:hover": { borderColor: BLUE_DARK, bgcolor: BLUE_PALE },
        }}
      >
        Print PDF
      </Button>
    </Stack>
  </Box>
);

/** The "nothing to show yet" / "nothing matched" panel inside a results card. */
export const EmptyState = ({ icon, children }) => (
  <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
    {icon
      ? React.cloneElement(icon, { sx: { fontSize: 44, color: BORDER, mb: 1 } })
      : null}
    <Typography sx={{ color: INK_MUTED, fontSize: "0.9rem" }}>
      {children}
    </Typography>
  </Box>
);

export const LoadingPanel = () => (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress sx={{ color: BLUE }} />
  </Box>
);

// ─── PDF ─────────────────────────────────────────────────────────────────────
/**
 * Opens a landscape PDF with the pawn reports' letterhead — store name and
 * address, the report title, and a right-aligned context line — and hands back
 * the doc plus the y to start the table at, so every export matches.
 */
export const startPdf = ({ store, title, context, orientation = "landscape" }) => {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;

  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text(store?.name || "Ledger Report", margin, 14);

  if (store?.address) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(String(store.address), margin, 19);
  }

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(String(title || "").toUpperCase(), margin, 27);

  if (context) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(String(context), pageWidth - margin, 27, { align: "right" });
  }

  return { doc, margin, pageWidth, startY: 32 };
};

/** The autoTable theme every pawn report prints with. */
export const pdfTableTheme = (margin) => ({
  theme: "grid",
  styles: {
    fontSize: 8,
    cellPadding: 1.5,
    lineColor: [226, 232, 240],
    lineWidth: 0.1,
    textColor: [15, 23, 42],
    valign: "middle",
  },
  headStyles: {
    fillColor: [37, 99, 235],
    textColor: [255, 255, 255],
    fontStyle: "bold",
    fontSize: 8,
    halign: "center",
    valign: "middle",
  },
  margin: { left: margin, right: margin },
});
